// The tour — the paged half of the help, for somebody who has never seen this
// screen.
//
// **It is not a replacement for `HELP_SECTIONS`, and the two are deliberately
// different shapes.** That one is a reference: somebody who has used the portal
// has one question, and a wizard makes them step past six answers to reach it.
// This is the opposite audience — a person opening a matter for the first time,
// who does not yet know what the questions are — and for them a list of
// sections is a wall. So the reference stays scrollable and this steps.
//
// **It runs on the matter screen and nowhere else.** Every topic worth a tour
// is here; the matter list is three controls and the reference already covers
// it. A tour that spanned both screens would have to stop half way and ask
// somebody to open a matter, which is the point at which people close it.
//
// **It highlights the real thing when the real thing is on screen, and reads as
// prose when it is not.** A matter with nothing compiled has no signing panel
// and no lot search, and a matter mid-import has no certificates — so a tour
// that insisted on its targets would be broken on exactly the matters a new
// starter opens first. The spotlight is an affordance; the words are the
// feature.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { T, seenOnce } from "./shared";
import { markSeen } from "./onboarding";

// Remembered per browser, so the nudge below offers itself once rather than on
// every matter for ever. The mechanism — and the reasoning about a browser that
// blocks site data — is `seenOnce` in `shared.js`, because the help panel
// introduces itself the same way and a second copy of that reversal is the one
// that gets it backwards.
//
// **And remembered in the office's own record beside it**, because this frame's
// `localStorage` is partitioned and evicted within days — `onboarding.js` has
// the whole reasoning. `useFirstVisit` is what reads the pair; the nudge's own
// gate is in `App.js`, where it is mounted.
export const TOUR_SEEN_KEY = "s32-tour-seen";

const tour = seenOnce(TOUR_SEEN_KEY);
export const tourSeen = tour.seen;
// **Both halves at the one door.** This is called from the tour's own finish
// and from the nudge's dismiss, and a server write bolted onto one call site
// is the call site somebody forgets.
export const rememberTour = () => { tour.remember(); markSeen("tour"); };
// Only for a test that needs a fresh browser's answer.
export const forgetTour = tour.forget;

// ── The steps ───────────────────────────────────────────────────────────────
//
// `pane` is the matter screen's own pane key — the tour sets it before it
// measures, because an inactive pane is `display: none` and has no rectangle to
// point at. `target` is a `data-tour` mark in App.js; a step with none, or one
// whose mark is not on this matter, simply reads centred.
//
// **The order is the order of the work**, not the order of the layout: what the
// matter needs, where the document is, how it is corrected, what it is built
// from, and how it goes out. Somebody following it is being taught a sequence
// they will repeat, which is the whole reason this is paged.
export const TOUR_STEPS = [
  {
    key: "welcome",
    pane: "contract",
    title: "This is where the contract is made now",
    body: [
      "Searches are still ordered in Triconvey, and the data entry there still has to be right. Everything after that happens here: the pack compiles itself when the last search lands, the contract is corrected on screen, the vendor signs, and it goes to the agent.",
      "This tour takes a couple of minutes, and it is in the menu at the top of every matter whenever you want it again. The counter above says how far through you are.",
    ],
  },
  {
    key: "next-step",
    pane: "contract",
    target: "next-step",
    title: "One card, saying what this matter needs",
    body: [
      "Whatever state a matter is in, this says the next thing to do and carries the button for it — Compile, Release, or nothing at all because something is still coming.",
      "Everything else that is true of the matter is listed quietly underneath rather than competing with it.",
    ],
  },
  {
    key: "rail",
    pane: "contract",
    target: "rail",
    title: "Contract, Searches, Activity",
    body: [
      "Contract is the document and the values behind it. Searches is what was ordered and what has come back. Activity is everything that has happened to the matter, in order.",
      "A red count on either of the first two is how many things want you. No count means nothing does.",
    ],
  },
  {
    key: "segment",
    pane: "contract",
    target: "segment",
    title: "Compiled and Draft are two views of one contract",
    body: [
      "Compiled is the PDF that was built — the document a purchaser would be handed. Draft is the live version, which moves the moment anything is corrected.",
    ],
  },
  {
    key: "draft",
    pane: "contract",
    target: "document",
    title: "The draft is edited on the page itself",
    body: [
      "Every highlighted value on the draft can be clicked, and a small editor opens over it. Clauses of the precedent can be reworded the same way, on this matter alone.",
      "A correction outranks the certificate and the client's form, and it survives every re-compile until somebody clears it.",
    ],
  },
  {
    key: "recompile",
    pane: "contract",
    target: "next-step",
    title: "A correction reaches the PDF on the next compile",
    body: [
      "The draft is live; the compiled PDF is a snapshot of when it was built. Correcting a value does not rewrite a PDF that already exists.",
      "Press Re-compile when the pack should be rebuilt around the change. The document panel says when the compiled copy is behind the draft, so it is never a guess.",
    ],
  },
  {
    key: "contract-data",
    pane: "contract",
    target: "contract-data",
    title: "Contract data mostly fills itself",
    body: [
      "These values are read off the certificates as they arrive and taken from the client's form. On most matters there is nothing here to type, which is why the panel opens showing only what is still missing.",
      "The agent is the exception, and it is worth checking on every matter: pick the agency from the directory where one is offered.",
    ],
  },
  {
    key: "conditions",
    pane: "contract",
    target: "conditions",
    title: "Special conditions, in three kinds",
    body: [
      "The precedent's own additional conditions — the unregistered plan, and the lease — are switched on here rather than found among a hundred fields.",
      "Underneath them are the conditions this office writes for the matter. Describe what a clause has to do and one will be drafted for you to read, change and accept; ask again to refine it. Nothing is saved until Save is pressed, so a drafted clause is a suggestion until somebody agrees with it.",
    ],
  },
  {
    // **The pane switch is shown rather than sprung.** Stepping from a panel on
    // Contract straight to one on Searches moved the whole screen under the
    // reader with nothing saying why — so the rail is pointed at while the
    // switch happens, and the next step lands on a pane they watched open.
    key: "to-searches",
    pane: "documents",
    target: "rail",
    title: "Now the other half — Searches",
    body: [
      "The rail has moved to Searches. Everything from here to the end of the pack is on this pane: what was ordered, what has come back, and what is still waiting.",
    ],
  },
  {
    key: "searches",
    pane: "documents",
    target: "searches",
    title: "What was ordered, and what is back",
    body: [
      "Each certificate is read as it arrives and what it says fills the contract. Nothing needs pressing — the list says which are read and which are still coming.",
      "Arrange documents sets the order they bind in, and takes one out of the pack without pretending it was never ordered.",
    ],
  },
  {
    key: "review",
    pane: "documents",
    target: "searches-review",
    title: "A heads-up on anything missing or contradictory",
    body: [
      "Before a statement goes out, this is what to read. A blank on the contract has two very different causes and it says which: a certificate nobody has ordered, or one sitting in the pack that did not state the thing it should have.",
      "It also reports where two sources disagree — a title and a client form naming different vendors, an address that has lost its unit number. It only ever reports; none of it stops a compile.",
    ],
  },
  {
    key: "planning",
    pane: "documents",
    target: "searches",
    title: "The planning property report arrives on its own",
    body: [
      "It is free, it does not go through Trisearch, and it is the only document that answers the designated bushfire prone area.",
      "So it is fetched from the state's own service as soon as the title gives us a parcel, and filed with everything else. Nobody has to remember it.",
    ],
  },
  {
    key: "add-document",
    pane: "documents",
    // The button itself where the panel is open, the panel where it is not: a
    // collapsed section still has a header to point at, and the step reads the
    // same either way.
    target: ["add-document", "searches"],
    title: "Add document, for anything that came another way",
    body: [
      "A certificate that arrived by post, a lease, an owners corporation report the vendor sent. Add document puts it in the pack and it binds like anything else.",
      "Documents added this way can be renamed, moved or removed. An ordered search cannot — taking one of those out would misstate what is outstanding.",
    ],
  },
  {
    key: "signing",
    pane: "contract",
    target: "signing",
    // **Not every matter has one of these, and saying so is the honest
    // answer.** A panel that only exists once a contract has been compiled is
    // absent on exactly the matter a new starter opens first, and a step that
    // described it anyway read as the tour pointing at something invisible.
    // Named per step rather than derived: what brings each one back is a
    // different press.
    absent: "This matter has nothing compiled yet, so the panel is not on screen. It appears under the document once you press Compile.",
    title: "Getting the vendor's signature",
    body: [
      "The vendor signs the statement before a purchaser is given it. This emails each vendor their own link, shows them the document rather than an attachment, and records who has signed.",
      "It is optional, and it is not the only way in — the agent can ask for the signature themselves from the page their download link opens.",
    ],
  },
  {
    key: "lot",
    pane: "contract",
    // The press itself, so the step knows whether this matter has one. Pointed
    // at the document instead, it would spotlight a panel that is always there
    // and say nothing about the button being absent from it.
    target: "find-lot",
    absent: "This matter has no compiled contract with a plan and an owners corporation certificate in it, so the button is not there. It sits above the compiled document on the ones that do.",
    title: "Find lot on plan",
    body: [
      "On an apartment the plan of subdivision runs to several sheets with sixty lots drawn across them, and finding the right one is the reason agents ring the office.",
      "This finds it and marks it, on every sheet it appears on. It runs by itself when the contract is released, and the mark shows on the agent's own download page.",
    ],
  },
  {
    key: "release",
    pane: "contract",
    target: "release",
    absent: "There is nothing to release until a contract has been compiled. The button appears on the card at the top of this page as soon as there is.",
    title: "Release sends it to the agent",
    body: [
      "The firm's covering letter, the contract, and a link that keeps working as the document is replaced — so a correction after the fact does not need a second email.",
      "Send for cross-check sends the same document to a colleague and is not a release: nothing about the matter changes, and nothing goes to the agent.",
    ],
  },
  {
    key: "menu",
    pane: "contract",
    target: "tour-menu",
    title: "The menu holds what this stage is not asking for",
    body: [
      "Re-read certificates asks for the pack to be read again — worth it after a certificate is replaced, and rarely otherwise, since each one is read as it arrives.",
      "Preview the agent's page shows exactly what the agent sees when they open their link: the contract, the summary, and the press that asks the vendor to sign. It is the one screen in the product nobody in the office otherwise sees.",
    ],
  },
  {
    key: "done",
    target: "tour-menu",
    title: "That is the whole of it",
    body: [
      "Order the searches and check the data entry in Triconvey. Compile, correct, sign and send here.",
      "This tour is in the menu at the top of every matter. How this works, on the matter list, is the same ground written out to be read rather than stepped through.",
    ],
  },
];

// ── Where the spotlight goes ────────────────────────────────────────────────

const PAD = 6;         // breathing room around the highlighted element
const GAP = 12;        // between the highlight and the panel
const PANEL_W = 380;
const MARGIN = 16;     // the panel never touches the edge of the window

// A step's targets, best first. Written as one name or a list; read as a list
// either way, so a caller never has to know which shape a step chose.
export const targetsOf = (step = {}) => (Array.isArray(step.target)
  ? step.target.filter(Boolean)
  : step.target ? [step.target] : []);

// A rectangle only counts when it has area. A pane that is `display: none`
// measures 0×0 and would otherwise put the spotlight in the top-left corner of
// the window, which reads as the tour pointing at nothing in particular.
const usableRect = (el) => {
  if (!el || typeof el.getBoundingClientRect !== "function") return null;
  const r = el.getBoundingClientRect();
  if (!r || r.width < 1 || r.height < 1) return null;
  return r;
};

// Below the target where there is room, above it where there is not, and
// centred when there is no target at all. Clamped on both axes, because a
// panel half off the screen is worse than one that is merely in the way.
export const placePanel = (rect, view, panelH = 260) => {
  const w = Math.min(PANEL_W, Math.max(240, view.width - MARGIN * 2));
  if (!rect) {
    return {
      left: Math.round((view.width - w) / 2),
      top: Math.round(Math.max(MARGIN, (view.height - panelH) / 2)),
      width: w,
    };
  }
  const below = rect.bottom + GAP;
  const above = rect.top - GAP - panelH;
  const top = below + panelH + MARGIN <= view.height
    ? below
    : above >= MARGIN
      ? above
      : Math.max(MARGIN, view.height - panelH - MARGIN);
  const left = Math.min(
    Math.max(MARGIN, rect.left),
    Math.max(MARGIN, view.width - w - MARGIN),
  );
  return { left: Math.round(left), top: Math.round(top), width: w };
};

// ── The overlay ─────────────────────────────────────────────────────────────

export const Tour = ({ steps = TOUR_STEPS, onClose, onPane, startAt = 0 }) => {
  const [i, setI] = useState(startAt);
  const [rect, setRect] = useState(null);
  const panelRef = useRef(null);
  const [panelH, setPanelH] = useState(260);

  const step = steps[Math.min(i, steps.length - 1)] || steps[0];
  const last = i >= steps.length - 1;

  const finish = useCallback(() => { rememberTour(); onClose?.(); }, [onClose]);

  // The pane is switched *before* anything is measured — see `usableRect`. It
  // is a separate effect from the measuring one so that a re-measure on scroll
  // cannot re-assert the pane under somebody who has clicked away from it.
  useEffect(() => {
    if (step.pane) onPane?.(step.pane);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.key]);

  // **A step may name more than one place to look, best first.** The Add
  // document button is inside a panel that may be closed, and a collapsed
  // section still has a header worth pointing at — so the step falls back
  // rather than losing its spotlight entirely.
  const findTarget = useCallback(() => {
    for (const name of targetsOf(step)) {
      const el = document.querySelector(`[data-tour="${name}"]`);
      if (usableRect(el)) return el;
    }
    return null;
  }, [step]);

  const measure = useCallback(() => {
    setRect(usableRect(findTarget()));
  }, [findTarget]);

  // Two frames rather than one: the pane above has only just been asked to
  // change, and React has to have committed that before the element it reveals
  // has a rectangle. A single frame measured the old layout on a pane switch.
  useLayoutEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        measure();
        const el = findTarget();
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
          // The scroll is smooth, so the rectangle it lands at is not the one
          // just measured. Re-reading after it settles is what stops the
          // spotlight trailing a step behind the page.
          setTimeout(measure, 380);
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [step.key, findTarget, measure]);

  useEffect(() => {
    const again = () => measure();
    window.addEventListener("scroll", again, true);
    window.addEventListener("resize", again);
    return () => {
      window.removeEventListener("scroll", again, true);
      window.removeEventListener("resize", again);
    };
  }, [measure]);

  useLayoutEffect(() => {
    const h = panelRef.current?.getBoundingClientRect?.().height;
    if (h) setPanelH(h);
  }, [step.key, rect]);

  // Escape leaves, the arrows move. Enter is Next because that is what a
  // person who has just read a panel presses.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); finish(); return; }
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        if (last) finish(); else setI(n => n + 1);
        return;
      }
      if (e.key === "ArrowLeft") { e.preventDefault(); setI(n => Math.max(0, n - 1)); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [last, finish]);

  const view = {
    width: typeof window === "undefined" ? 1024 : window.innerWidth,
    height: typeof window === "undefined" ? 768 : window.innerHeight,
  };
  const at = placePanel(rect, view, panelH);

  return (
    <div role="dialog" aria-label="Portal tour"
      // **The screen underneath stays live, and that is the whole posture.**
      // A tour that points at Arrange documents and then swallows the click is
      // worse than no tour, and half these steps are worth reading with the
      // thing they describe actually open. So the container passes clicks
      // through and only the panel takes them.
      //
      // `aria-modal` is off for the same reason: it would be a lie about a
      // dialog that deliberately does not trap anything.
      style={{ position: "fixed", inset: 0, zIndex: 400, pointerEvents: "none" }}>

      {/* **The dim is a shadow on the hole, not a sheet with a hole in it.**
          One absolutely positioned box with a very large spread shadow gives a
          real cut-out with no SVG mask and no four-rectangle arithmetic to get
          wrong at the edges. With no target it covers the window, which is the
          same element doing the plain backdrop. */}
      {/* Clicking the dim does not close — there are three ways out (Skip,
          Done, Escape) and none of them is a click somebody might have meant
          for the page. A backdrop that closed on a stray click, on a screen
          that is still live, would drop the tour mid-step for nothing. */}
      <div
        aria-hidden="true"
        style={rect ? {
          position: "fixed",
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 12,
          boxShadow: "0 0 0 9999px rgba(51,51,51,0.55)",
          border: `2px solid ${T.red}`,
          pointerEvents: "none",
          transition: "top 0.18s, left 0.18s, width 0.18s, height 0.18s",
        } : {
          position: "fixed", inset: 0, background: "rgba(51,51,51,0.55)",
          pointerEvents: "none",
        }}
      />

      <div ref={panelRef}
        style={{
          position: "fixed", top: at.top, left: at.left, width: at.width,
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
          boxShadow: "0 16px 48px rgba(0,0,0,0.22)", padding: 18,
          fontFamily: "Inter, sans-serif",
          // The one part of the overlay that is not transparent to a click.
          pointerEvents: "auto",
        }}>

        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: T.textFaint, fontWeight: 500, letterSpacing: "0.02em" }}>
            {i + 1} of {steps.length}
          </span>
          <button onClick={finish}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.textFaint, fontSize: 11.5, fontFamily: "Inter, sans-serif", padding: 0 }}>
            Skip the tour
          </button>
        </div>

        <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: 15.5, color: T.text, margin: "0 0 8px", letterSpacing: "-0.01em" }}>
          {step.title}
        </h3>

        {step.body.map((p, n) => (
          <p key={n} style={{ fontSize: 12.5, color: T.textSoft, margin: "0 0 8px", lineHeight: 1.6 }}>{p}</p>
        ))}

        {/* **What is not on this matter, said rather than left to be noticed.**
            The step still reads — a new starter should learn that signing and
            releasing exist before they meet one — but a panel describing a
            button nobody can see is the confusion this removes. Shown only
            where the step names a consequence and its target really is absent,
            so it never appears on a matter that has everything. */}
        {step.absent && !rect && (
          <p style={{
            fontSize: 12, color: T.textSoft, margin: "0 0 8px", lineHeight: 1.55,
            background: T.bgAlt, borderRadius: 8, padding: "9px 11px",
          }}>
            <strong style={{ color: T.text, fontWeight: 500 }}>Not on this matter yet. </strong>
            {step.absent}
          </p>
        )}

        {/* The rail of dots is how long this is, which is the question somebody
            two steps in is actually asking. Not clickable: seventeen four-pixel
            targets is a row of misses, and Back is right there. */}
        <div style={{ display: "flex", gap: 4, margin: "12px 0 14px" }}>
          {steps.map((s, n) => (
            <span key={s.key} style={{
              height: 3, flex: 1, borderRadius: 999,
              background: n <= i ? T.text : T.bgAlt,
            }} />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button onClick={() => setI(n => Math.max(0, n - 1))} disabled={i === 0}
            style={{
              background: "none", border: "none", fontFamily: "Inter, sans-serif",
              fontSize: 12.5, color: i === 0 ? T.bgAlt : T.textSoft,
              cursor: i === 0 ? "default" : "pointer", padding: "6px 2px",
            }}>
            Back
          </button>
          <button onClick={() => (last ? finish() : setI(n => n + 1))}
            style={{
              background: T.text, color: T.white, border: "none", borderRadius: 999,
              padding: "8px 18px", fontSize: 12.5, fontWeight: 500,
              fontFamily: "Inter, sans-serif", cursor: "pointer",
            }}>
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── The offer ───────────────────────────────────────────────────────────────
//
// **Offered once, never sprung.** Opening the tour on somebody's first matter
// would take the screen away from a person who came to do a job — and the one
// thing worse than a tour nobody finds is a tour that interrupts. So this is a
// small card in the corner, it appears only until somebody answers it either
// way, and dismissing it counts as having seen the tour.
export const TourNudge = ({ onStart, onDismiss }) => (
  <div style={{
    position: "fixed", left: 20, bottom: 20, zIndex: 250, width: 288,
    background: T.white, border: `1px solid ${T.border}`, borderRadius: 12,
    boxShadow: "0 12px 32px rgba(51,51,51,0.16)", padding: 16,
    fontFamily: "Inter, sans-serif",
  }}>
    <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 5 }}>
      New to this screen?
    </div>
    <p style={{ fontSize: 12, color: T.textSoft, margin: "0 0 12px", lineHeight: 1.55 }}>
      A short tour of where the contract is corrected, compiled, signed and sent.
      Two minutes, and you can leave it at any point.
    </p>
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <button onClick={onStart} style={{
        background: T.text, color: T.white, border: "none", borderRadius: 999,
        padding: "7px 15px", fontSize: 12, fontWeight: 500,
        fontFamily: "Inter, sans-serif", cursor: "pointer",
      }}>
        Take the tour
      </button>
      <button onClick={onDismiss} style={{
        background: "none", border: "none", color: T.textFaint, fontSize: 12,
        fontFamily: "Inter, sans-serif", cursor: "pointer", padding: "7px 4px",
      }}>
        Not now
      </button>
    </div>
  </div>
);

export default Tour;
